import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Mail } from "lucide-react";
import {
  notificationPreviewApi,
  notificationSettingsQueryKeys,
  notificationTemplateToolsApi,
  type MessagingTemplate,
  type NotificationEventRow,
  type ReplaceProblem,
} from "../../shared/api/notificationSettings";
import { apiError } from "../../shared/api/client";
import { meApi, meQueryKeys } from "../../shared/api/me";
import { approvedTemplatesForChannel } from "./NotificationsSettingsPage";
import { templateName } from "./EventChannelTemplates";

/** Filled by Addy Message from the clinic's profile — no event supplies them (D-108). */
const PLATFORM_FILLED = new Set(["clinic.name", "clinic.phone", "clinic.address"]);

export type PreviewSource = "system" | "platform" | "fixed" | "none";

export interface PreviewRow {
  param: string;
  source: PreviewSource;
  label: string;
  value: string | null;
}

/**
 * What each parameter of the chosen template will carry (#19567) — built here from what the screen
 * already holds (the template's parameters, the event's fields, the catalogue's examples, the
 * cell's fixed values), because Addy Message has no render endpoint for API keys yet.
 *
 * <p>Example values only: the preview shows the shape of a message, never a patient's data.
 */
export function previewRows(
  template: MessagingTemplate | undefined,
  channel: string,
  event: NotificationEventRow,
  staticParams: Record<string, string>,
  labels: Record<string, string>,
  examples: Record<string, string>,
): PreviewRow[] {
  const rows: PreviewRow[] = [];
  const rendition = template?.renditions.find((r) => r.channel === channel);
  for (const p of rendition?.params ?? []) {
    const field = p.canonicalKey ?? p.key;
    if (event.suppliedParams.includes(field)) {
      rows.push({ param: p.key, source: "system", label: labels[field] ?? field, value: examples[field] ?? null });
    } else if (PLATFORM_FILLED.has(field)) {
      rows.push({ param: p.key, source: "platform", label: labels[field] ?? field, value: null });
    } else if (staticParams[p.key] ?? staticParams[field]) {
      rows.push({ param: p.key, source: "fixed", label: "Giá trị cố định", value: staticParams[p.key] ?? staticParams[field] });
    } else {
      rows.push({ param: p.key, source: "none", label: "Chưa có giá trị", value: null });
    }
  }
  return rows;
}

const SOURCE_TEXT: Record<PreviewSource, string> = {
  system: "🟢 Theo từng bệnh nhân",
  platform: "🔵 Từ hồ sơ phòng khám",
  fixed: "🟠 Cố định (mọi tin như nhau)",
  none: "⚠️ Thiếu",
};

/**
 * Why a stored template can no longer go out on a channel, or null when it still can (#19576).
 *
 * <p>The same order as the server's readiness check, plus the template-level switch the server
 * leaves to Addy Message's send-time refusal (`TEMPLATE_DISABLED`, status 0). A rendition Zalo
 * disabled or deleted comes back as DRAFT (Addy Message `ZaloTemplateWriter`), which is why DRAFT
 * reads "Zalo đã tắt" on ZBS.
 */
export function stoppedReason(
  code: string,
  templates: MessagingTemplate[],
  channel: string,
): string | null {
  const t = templates.find((x) => x.code === code);
  if (!t) return "không còn tồn tại";
  if (t.archived) return "đã lưu trữ";
  if (t.status === 0) return "đã bị tắt";
  const r = t.renditions.find((x) => x.channel === channel);
  if (r?.approvalStatus === "APPROVED") return null;
  const via = channel === "ZBS" ? "Zalo" : "nhà cung cấp";
  if (!r || r.approvalStatus === "DRAFT")
    return channel === "ZBS" ? "đã bị Zalo tắt hoặc chưa gửi duyệt" : "chưa được duyệt";
  if (r.approvalStatus === "REJECTED") return `bị ${via} từ chối`;
  return `đang chờ ${via} duyệt`;
}

/**
 * Tools under a chosen template (#19567): what the message will carry, and a test send to the
 * user's own phone (Zalo only). Where else the template is used is not shown here (#19576) — it
 * only matters once the template stops, see {@link StoppedTemplateWarning}.
 */
export function TemplateTools({
  event,
  channel,
  serviceId,
  template,
  staticParams,
  labels,
  examples,
  showPreview = true,
}: {
  event: NotificationEventRow;
  channel: string;
  serviceId: string | null;
  template: MessagingTemplate | undefined;
  staticParams: Record<string, string>;
  labels: Record<string, string>;
  examples: Record<string, string>;
  /** #19586 — off where "Tham số của mẫu" (the platform preview) already shows the same data. */
  showPreview?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const testSend = useMutation({
    mutationFn: () => notificationTemplateToolsApi.testSend(event.eventKey, { channel, serviceId }),
    onSuccess: (r) =>
      setTestResult({
        ok: true,
        text: `Đã gửi thử mẫu ${r.templateCode} tới ${r.sentTo}.`,
      }),
    onError: (e) => setTestResult({ ok: false, text: apiError(e).detail }),
  });

  if (!template) return null;
  if (channel === "EMAIL") {
    return <EmailTools event={event} serviceId={serviceId} showPreview={showPreview} />;
  }
  const rows = previewRows(template, channel, event, staticParams, labels, examples);

  return (
    <div className="grid gap-1 text-xs" data-testid="template-tools">
      <div className="flex flex-wrap gap-x-3">
        {showPreview && (
          <button
            type="button"
            className="text-action underline-offset-2 hover:underline"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? "Ẩn xem trước" : "Xem trước nội dung"}
          </button>
        )}
        {channel === "ZBS" && (
          <button
            type="button"
            className="text-action underline-offset-2 hover:underline disabled:opacity-50"
            disabled={testSend.isPending}
            title="Gửi bằng chế độ thử của Zalo (miễn phí) tới số điện thoại trong hồ sơ của bạn — số phải là tester/admin của OA."
            onClick={() => {
              setTestResult(null);
              testSend.mutate();
            }}
          >
            {testSend.isPending ? "Đang gửi thử…" : "Gửi thử tới số của tôi"}
          </button>
        )}
      </div>

      {testResult && (
        <p
          role="status"
          className={testResult.ok ? "text-emerald-700" : "text-destructive"}
          data-testid="test-send-result"
        >
          {testResult.text}
        </p>
      )}

      {showPreview && open && channel === "SMS" && (
        <SmsPreview eventKey={event.eventKey} serviceId={serviceId} />
      )}

      {showPreview && open && channel !== "SMS" && (
        <table className="w-full border text-left" data-testid="template-preview">
          <thead>
            <tr className="bg-muted/50">
              <th className="px-1 py-0.5 font-medium">Tham số Zalo</th>
              <th className="px-1 py-0.5 font-medium">Nguồn</th>
              <th className="px-1 py-0.5 font-medium">Giá trị ví dụ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.param} className={"border-t" + (r.source === "none" ? " text-destructive" : "")}>
                <td className="px-1 py-0.5 font-mono">{r.param}</td>
                <td className="px-1 py-0.5">
                  {SOURCE_TEXT[r.source]}
                  {r.source === "system" || r.source === "platform" ? ` — ${r.label}` : ""}
                </td>
                <td className="px-1 py-0.5">
                  {r.value ?? (r.source === "platform" ? "(từ hồ sơ phòng khám)" : "—")}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-1 py-0.5 text-muted-foreground">
                  Mẫu không khai báo tham số cho kênh này.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** "l***@phongkham.vn" — enough to recognise one's own address without printing it whole. */
export function maskEmail(email: string | null | undefined): string | null {
  const e = (email ?? "").trim();
  const at = e.indexOf("@");
  if (at < 1) return null;
  return `${e[0]}***${e.slice(at)}`;
}

/**
 * E-mail under a chosen template, laid out like Zalo's "Bệnh nhân nhận" (#19587): the letter folded
 * behind a visible card, and a test send that names the address it goes to and says under it where
 * that address comes from.
 */
function EmailTools({
  event,
  serviceId,
  showPreview,
}: {
  event: NotificationEventRow;
  serviceId: string | null;
  showPreview: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const me = useQuery({
    queryKey: meQueryKeys.profile(),
    queryFn: () => meApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const masked = maskEmail(me.data?.email);
  const testSend = useMutation({
    mutationFn: () =>
      notificationTemplateToolsApi.testSend(event.eventKey, { channel: "EMAIL", serviceId }),
    onSuccess: (r) =>
      setResult({
        ok: true,
        // Addy Message has TAKEN the letter — not the SMTP server, not the inbox (#19641).
        text: `Addy Message đã nhận thư thử ${r.templateCode} gửi tới ${r.sentTo}. Kiểm tra hộp thư (cả mục Spam) sau ít phút; không thấy thư thì xem kết nối ở Máy chủ Email bên Addy Message.`,
      }),
    onError: (e) => setResult({ ok: false, text: apiError(e).detail }),
  });
  // Đọc hồ sơ THẤT BẠI khác hẳn hồ sơ KHÔNG CÓ email (#19641 review): cái đầu là lỗi của chúng ta,
  // cái sau là việc người dùng phải làm. Gộp lại thì màn hình nói một điều không đúng về tài khoản
  // của họ và họ đi sửa một thứ vốn không sai.
  const unknownEmail = me.isError;
  const noEmail = !me.isLoading && !me.isError && !masked;

  return (
    <div className="grid content-start gap-2 text-xs" data-testid="template-tools">
      {showPreview &&
        (open ? (
          <button
            type="button"
            className="flex items-center gap-1 justify-self-start text-xs text-action underline-offset-2 hover:underline"
            aria-expanded
            onClick={() => setOpen(false)}
          >
            <ChevronUp className="h-3.5 w-3.5" aria-hidden />
            Thu gọn hình ảnh
          </button>
        ) : (
          <button
            type="button"
            className="flex w-full max-w-[400px] items-center gap-3 rounded-md border border-dashed bg-muted/30 px-3 py-2.5 text-left hover:border-action hover:bg-muted/60"
            aria-expanded={false}
            onClick={() => setOpen(true)}
          >
            <Mail className="h-6 w-6 shrink-0 text-action" aria-hidden />
            <span className="grid min-w-0 flex-1">
              <span className="text-sm font-medium text-action">Xem hình ảnh thư</span>
              <span className="text-xs text-muted-foreground">
                Thư bệnh nhân nhận trong hộp thư trông như thế nào
              </span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          </button>
        ))}
      {showPreview && open && <EmailPreview eventKey={event.eventKey} serviceId={serviceId} />}

      <div className="grid gap-1">
        <button
          type="button"
          className="h-8 justify-self-start rounded-md border px-3 text-xs font-medium text-action hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          disabled={noEmail || unknownEmail || testSend.isPending || me.isLoading}
          onClick={() => {
            setResult(null);
            testSend.mutate();
          }}
        >
          {testSend.isPending ? "Đang gửi thử…" : `Gửi thử tới email ${masked ?? ""}`.trim()}
        </button>
        {unknownEmail ? (
          <small className="text-xs text-muted-foreground" data-testid="test-send-unknown-email">
            Chưa đọc được hồ sơ cá nhân nên không biết gửi thử tới đâu. Tải lại trang; nếu vẫn vậy
            thì đây là lỗi hệ thống, không phải tài khoản của bạn.
          </small>
        ) : noEmail ? (
          <small className="text-xs text-muted-foreground" data-testid="test-send-disabled">
            Tài khoản của bạn chưa có địa chỉ email.{" "}
            <a href="/me/profile" className="text-action underline">
              Cập nhật ở Hồ sơ cá nhân
            </a>
          </small>
        ) : (
          <small className="text-xs text-muted-foreground">
            Gửi một thư thật, với dữ liệu ví dụ, qua máy chủ Email của phòng khám tới địa chỉ email
            trong{" "}
            <a href="/me/profile" className="text-action underline-offset-2 hover:underline">
              Hồ sơ cá nhân
            </a>{" "}
            của bạn.
            {serviceId ? " Theo cấu hình của dịch vụ này." : ""}
          </small>
        )}
        {result && (
          <small
            role="status"
            className={"text-xs " + (result.ok ? "text-emerald-700" : "text-destructive")}
            data-testid="test-send-result"
          >
            {result.text}
          </small>
        )}
      </div>
    </div>
  );
}

/**
 * The letter an e-mail from this cell would carry (#19641), built by Addy Message's own send rules
 * with example data — the subject, and the HTML in a sandboxed frame (no scripts, no same-origin).
 * A variable the event cannot fill goes out BLANK, which is said here rather than discovered by a
 * patient; an unknown placeholder stops the send, which is said louder.
 */
function EmailPreview({ eventKey, serviceId }: { eventKey: string; serviceId: string | null }) {
  const q = useQuery({
    queryKey: ["notification-email-preview", eventKey, serviceId],
    queryFn: () => notificationPreviewApi.preview(eventKey, { channel: "EMAIL", serviceId }),
    retry: false,
  });
  if (q.isLoading) {
    return <p className="text-muted-foreground">Đang dựng thư…</p>;
  }
  if (q.isError) {
    return (
      <p className="text-destructive" role="alert">
        {apiError(q.error).detail}
      </p>
    );
  }
  const p = q.data;
  if (!p) return null;
  return (
    <div className="grid gap-1" data-testid="email-preview">
      {p.missing.length > 0 && (
        <p className="text-destructive" role="alert">
          ⛔ Mẫu có biến không tồn tại — thư sẽ không gửi được: {p.missing.join(", ")}
        </p>
      )}
      {(p.blank ?? []).length > 0 && (
        <p className="text-amber-800 dark:text-amber-400">
          Các biến sẽ để trống khi sự kiện không có dữ liệu: {(p.blank ?? []).join(", ")}
        </p>
      )}
      <p>
        <span className="text-muted-foreground">Tiêu đề: </span>
        <b data-testid="email-preview-subject">{p.subject || "(không có tiêu đề)"}</b>
      </p>
      <iframe
        title="Xem trước email"
        sandbox=""
        srcDoc={p.bodyHtml ?? ""}
        className="h-72 w-full rounded border bg-white"
        data-testid="email-preview-body"
      />
      <p className="text-muted-foreground">Dữ liệu ví dụ — không phải dữ liệu bệnh nhân.</p>
    </div>
  );
}

/**
 * The text an SMS from this cell would carry (#19667), rendered by Addy Message's own send rules
 * with example data, and what it costs: the carriers bill per segment, and a reminder with
 * diacritics fits 70 characters per segment instead of 160.
 */
export function SmsPreview({ eventKey, serviceId }: { eventKey: string; serviceId: string | null }) {
  const q = useQuery({
    queryKey: ["notification-sms-preview", eventKey, serviceId],
    queryFn: () => notificationPreviewApi.preview(eventKey, { channel: "SMS", serviceId }),
    retry: false,
  });
  if (q.isLoading) {
    return <p className="text-muted-foreground">Đang dựng tin…</p>;
  }
  if (q.isError) {
    return (
      <p className="text-destructive" role="alert">
        {apiError(q.error).detail}
      </p>
    );
  }
  const p = q.data;
  if (!p) return null;
  const segments = p.smsSegments ?? 0;
  return (
    <div className="grid gap-1" data-testid="sms-preview">
      {p.missing.length > 0 && (
        <p className="text-destructive" role="alert">
          ⛔ Thiếu dữ liệu cho: {p.missing.join(", ")} — tin SMS sẽ không gửi được.
        </p>
      )}
      <pre className="whitespace-pre-wrap rounded border bg-muted/30 p-2 font-sans" data-testid="sms-preview-text">
        {p.bodyText}
      </pre>
      <p className={segments > 1 ? "text-amber-800 dark:text-amber-400" : "text-muted-foreground"} data-testid="sms-preview-segments">
        {p.smsUnits ?? 0} ký tự · {segments} tin SMS ({p.smsEncoding === "UCS2" ? "có dấu: 70 ký tự/tin" : "không dấu: 160 ký tự/tin"})
        {segments > 1 && " — mỗi tin tính phí riêng"}
      </p>
      <p className="text-muted-foreground">Dữ liệu ví dụ — không phải dữ liệu bệnh nhân.</p>
    </div>
  );
}

/**
 * The stored template has stopped (#19576): Zalo switched it off, it is not approved, archived, or
 * gone. Only then does the clinic need to know where else it is used — every one of those places
 * went silent at the same moment — with a one-step swap for all of them, because an approved Zalo
 * template can never be edited, only replaced.
 */
export function StoppedTemplateWarning({
  code,
  reason,
  channel,
  templates,
}: {
  code: string;
  reason: string;
  channel: string;
  templates: MessagingTemplate[];
}) {
  const qc = useQueryClient();
  const [replaceTo, setReplaceTo] = useState("");
  const [replaceResult, setReplaceResult] = useState<{
    ok: boolean;
    text: string;
    problems?: ReplaceProblem[];
  } | null>(null);

  const usagesQ = useQuery({
    queryKey: notificationSettingsQueryKeys.usages(code),
    queryFn: () => notificationTemplateToolsApi.usages(code),
  });

  const replace = useMutation({
    mutationFn: () => notificationTemplateToolsApi.replace(code, replaceTo),
    onSuccess: (r) => {
      setReplaceResult({ ok: true, text: `Đã thay ở ${r.replaced} nơi.` });
      setReplaceTo("");
      qc.invalidateQueries({ queryKey: notificationSettingsQueryKeys.events() });
      qc.invalidateQueries({ queryKey: ["clinic-notification-cells"] });
      qc.invalidateQueries({ queryKey: ["clinic-notification-template-usages"] });
    },
    onError: (e) => {
      const err = apiError(e);
      const body = err.problem as unknown as {
        problems?: ReplaceProblem[];
        properties?: { problems?: ReplaceProblem[] };
      } | null;
      setReplaceResult({
        ok: false,
        text: err.detail,
        problems: body?.problems ?? body?.properties?.problems,
      });
    },
  });

  // A template that no longer exists has no name to show; its code is all the clinic can match.
  const name = templateName(templates.find((t) => t.code === code), channel) ?? code;
  const candidates = approvedTemplatesForChannel(templates, channel).filter(
    (t) => !t.archived && t.status !== 0 && t.code !== code,
  );
  const count = usagesQ.data?.length ?? 0;

  return (
    <div
      className="grid gap-1 rounded border border-amber-300 bg-amber-50 p-1.5 text-xs text-amber-900"
      role="alert"
      data-testid="stopped-template"
    >
      <span>
        ⚠️ Mẫu đang dùng <b>{name}</b> {reason} — tin sẽ không gửi được.
        {count > 0 && (
          <>
            {" "}
            Mẫu này đang dùng ở <b>{count} nơi</b>.
          </>
        )}
      </span>
      {usagesQ.isError && (
        <span className="text-destructive">Không tải được số nơi đang dùng mẫu này.</span>
      )}
      {count > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span>Thay bằng</span>
          <select
            className="h-7 min-w-0 max-w-full rounded border bg-background px-1"
            value={replaceTo}
            onChange={(e) => {
              setReplaceTo(e.target.value);
              setReplaceResult(null);
            }}
            aria-label="Mẫu thay thế"
          >
            <option value="">— chọn mẫu mới —</option>
            {candidates.map((t) => (
              <option key={t.code} value={t.code}>
                {templateName(t, channel) ?? t.code}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="h-7 rounded border bg-background px-2 disabled:opacity-50"
            disabled={!replaceTo || replace.isPending}
            onClick={() => replace.mutate()}
          >
            {replace.isPending ? "Đang thay…" : `Thay ở cả ${count} nơi`}
          </button>
        </div>
      )}
      {count > 0 && (
        <span className="text-amber-800">
          Thay tất cả hoặc không thay gì. Giá trị cố định của mẫu cũ không được mang sang.
        </span>
      )}
      {usagesQ.data && count === 0 && <span>Hãy chọn mẫu khác ở ô bên trên.</span>}
      {replaceResult && (
        <div className={replaceResult.ok ? "text-emerald-700" : "text-destructive"} role="status">
          {replaceResult.text}
          {replaceResult.problems && (
            <ul className="list-disc pl-4">
              {replaceResult.problems.map((p, i) => (
                <li key={i}>
                  {p.eventLabel}
                  {p.channel ? ` (${p.channel})` : ""}: {p.problem}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
